import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AgentDemo, AgentDemoAnswer, AgentDemoGap } from "@cloudox/api-client";
import {
  AgentTurn,
  Sheet,
  TechnicalDetails,
  useMediaQuery,
  type AgentTurnRecord,
} from "@cloudox/ui";
import { askSearch } from "@/lib/preview";
import { splitSparseMarkdownTables } from "@/lib/splitSparseMarkdownTables";

/** Suggestions shown before the reader asks for the rest. */
const VISIBLE_QUESTIONS = 3;

const PERSONA_LABEL: Record<AgentDemoAnswer["persona"], string> = {
  generic: "Generic",
  security: "Security",
  architect: "Architect",
  operations: "Operations",
  finops: "FinOps",
};

export interface AgentDemoPanelProps {
  readonly demo: AgentDemo;
  /** The selected question's key, or `""` for the suggestion list. */
  readonly questionKey: string;
  readonly onClose: () => void;
}

/**
 * The CDX Agent, as a public reader can meet it.
 *
 * A panel beside the report rather than a page of its own, because that is
 * where the Agent lives in the product: a reader asks about the environment
 * they are currently looking at, and sending them to a separate document to do
 * it breaks the one thing the Agent is for. Above `xl` it is a sticky column;
 * below, a focus-managed sheet over the page — the same two layouts, and the
 * same breakpoint, as the console's own assistant.
 *
 * The answers are recorded rather than live, and that is disclosed once, in
 * one place, rather than repeated around every response. A banner over each
 * answer would be read as a caveat about *that answer's* trustworthiness,
 * which is precisely the wrong thing to say about output that is real.
 */
export function AgentDemoPanel({ demo, questionKey, onClose }: AgentDemoPanelProps) {
  const wide = useMediaQuery("(min-width: 1280px)");
  const body = <PanelBody demo={demo} questionKey={questionKey} />;

  if (!wide) {
    return (
      <Sheet
        title="CDX Agent"
        onClose={onClose}
        closeLabel="Close CDX Agent"
        widthClass="w-full max-w-md"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{body}</div>
      </Sheet>
    );
  }

  return (
    <aside
      className="hidden w-[26rem] shrink-0 xl:block"
      aria-label="CDX Agent"
      style={{ borderLeft: "1px solid var(--border)", backgroundColor: "var(--bg-sidebar)" }}
    >
      <div className="sticky top-14 flex h-[calc(100vh-3.5rem)] flex-col">
        <div
          className="flex h-12 shrink-0 items-center justify-between border-b px-4"
          style={{ borderColor: "var(--border)" }}
        >
          <span className="text-sm font-semibold" style={{ color: "var(--fg)" }}>
            CDX Agent
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Close CDX Agent"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{body}</div>
      </div>
    </aside>
  );
}

function PanelBody({
  demo,
  questionKey,
}: {
  readonly demo: AgentDemo;
  readonly questionKey: string;
}) {
  const answer = questionKey ? demo.answers.find((a) => a.key === questionKey) : undefined;
  return (
    <div className="space-y-4">
      <Intro />
      {questionKey && !answer ? <UnknownQuestion questionKey={questionKey} /> : null}
      {answer ? <AnswerSection demo={demo} answer={answer} /> : <Suggestions demo={demo} />}
    </div>
  );
}

/**
 * What the Agent is, and what this particular instance of it is.
 *
 * Two sentences of product, then everything about *this recording* folded into
 * one disclosure. The split is the point: a reader deciding whether the Agent
 * is worth anything needs the first; a reader checking whether they are being
 * sold a mock-up needs the second, and can open it.
 */
function Intro() {
  return (
    <section className="space-y-2">
      <p className="text-[13px] leading-relaxed" style={{ color: "var(--fg-muted)" }}>
        CDX Agent answers questions using evidence retrieved from the environment&rsquo;s knowledge
        graph. In the live product, it can add AI narration, validates claims against the evidence,
        and cites the supporting records.
      </p>
      <TechnicalDetails summary="About these answers">
        <div className="space-y-2 text-xs leading-relaxed" style={{ color: "var(--fg-muted)" }}>
          <p>
            These preview answers were generated through CDX Agent&rsquo;s verified deterministic
            path using this sanitized demo environment. No model or API is called when you select a
            question.
          </p>
          <p>
            The authenticated product supports free-text questions and validated AI-narrated
            answers.
          </p>
        </div>
      </TechnicalDetails>
    </section>
  );
}

function Suggestions({ demo }: { readonly demo: AgentDemo }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? demo.answers : demo.answers.slice(0, VISIBLE_QUESTIONS);
  const hidden = demo.answers.length - visible.length;

  return (
    <section aria-labelledby="agent-questions-heading" className="space-y-2">
      <h2
        id="agent-questions-heading"
        className="text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: "var(--fg-subtle)" }}
      >
        Suggested questions
      </h2>
      <ul className="space-y-1.5">
        {visible.map((answer) => (
          <li key={answer.key} className="list-none">
            <Link
              to={askSearch(answer.key)}
              className="flex flex-col rounded-lg border px-3 py-2 transition-colors"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--bg-elevated)",
                textDecoration: "none",
                color: "var(--fg)",
              }}
            >
              <span className="text-[13px] font-medium leading-snug">{answer.question}</span>
              <span className="mt-1 text-[11px]" style={{ color: "var(--fg-subtle)" }}>
                {PERSONA_LABEL[answer.persona]}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {demo.answers.length > VISIBLE_QUESTIONS ? (
        <button
          type="button"
          className="btn btn-ghost text-xs"
          style={{ border: "1px solid var(--border)" }}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show fewer" : `Show all ${demo.answers.length} questions`}
        </button>
      ) : null}
      {hidden > 0 ? (
        <p className="sr-only" aria-live="polite">
          {hidden} further curated questions are available.
        </p>
      ) : null}
    </section>
  );
}

function AnswerSection({
  demo,
  answer,
}: {
  readonly demo: AgentDemo;
  readonly answer: AgentDemoAnswer;
}) {
  const turn = useMemo(() => toTurn(answer), [answer]);
  const others = demo.answers.filter((a) => a.key !== answer.key);

  return (
    <div className="space-y-3">
      <Link
        to={askSearch()}
        className="inline-flex text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: "var(--fg-subtle)", textDecoration: "none" }}
      >
        &larr; All questions
      </Link>

      <AgentTurn turn={turn} />

      {others.length > 0 ? (
        <nav
          className="space-y-1.5 border-t pt-3"
          style={{ borderColor: "var(--border)" }}
          aria-label="Other curated questions"
        >
          <h2
            className="text-[11px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: "var(--fg-subtle)" }}
          >
            Ask something else
          </h2>
          <ul className="space-y-1">
            {others.slice(0, VISIBLE_QUESTIONS).map((other) => (
              <li key={other.key} className="list-none">
                <Link
                  to={askSearch(other.key)}
                  className="block rounded-md px-2 py-1.5 text-[13px]"
                  style={{ color: "var(--fg-muted)", textDecoration: "none" }}
                >
                  {other.question}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </div>
  );
}

function UnknownQuestion({ questionKey }: { readonly questionKey: string }) {
  return (
    <p
      className="rounded-md border px-3 py-2 text-xs"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--bg-muted)",
        color: "var(--fg-muted)",
      }}
      role="status"
    >
      &ldquo;{questionKey}&rdquo; is not one of the curated questions in this preview.
    </p>
  );
}

/**
 * Present a recorded fixture as the turn shape the shared renderer takes.
 *
 * A mapping, not a transformation: every field is carried across as recorded.
 * The one adjustment is to the Markdown, where {@link splitSparseMarkdownTables}
 * separates a summary row from its detail rows — a layout fix to a table the
 * Agent emitted as one block, which changes how it reads and not what it says.
 *
 * Citations arrive as bare record ids because that is all the fixture carries;
 * the renderer draws them without a confidence badge rather than borrowing the
 * answer's.
 */
function toTurn(answer: AgentDemoAnswer): AgentTurnRecord {
  return {
    question: answer.question,
    answer_markdown: splitSparseMarkdownTables(answer.markdown),
    confidence: answer.confidence,
    ...(answer.confidenceBasis ? { confidence_basis: answer.confidenceBasis } : {}),
    assumptions: answer.assumptions,
    unknowns: answer.unknowns,
    gaps: answer.gaps.map(formatGap),
    citations: answer.citedRecordIds.map((id) => ({ record_id: id })),
  };
}

function formatGap(gap: AgentDemoGap): string {
  const reason = gap.reason.replace(/_/g, " ");
  return gap.detail ? `${reason} — ${gap.detail}` : reason;
}
